import { Fragment, useCallback, useContext, useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'

import { PlanningBasicsStep } from '../formSteps/PlanningBasicStep/PlanningBasicsStep'

import { Backdrop, CircularProgress, Container, IconButton, StepConnector, StepIconProps } from '@mui/material'
import Typography from '@mui/material/Typography'
import { historyRead, saveDraft, submitExperiment, updateExperimentFormStep } from '../../api'
import { AppStateContext } from '../../state/AppProvider'
import CustomDialog from '../dialogs/CustomDialog/CustomDialog'
import { AnalysisAndResultsStep } from '../formSteps/AnalysisAndResultsStep'
import { SetupDetailsStep } from '../formSteps/SetupDetailsStep'
import styles from './ExperimentForm.module.css'

import { get, isEmpty, isNil } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import BlueDot from '../../assets/blue_dot.svg'
import CollapseButton from '../../assets/collapse_button.svg'
import EditSquareIcon from '../../assets/edit_square.svg'
import GrayDot from '../../assets/gray_dot.svg'
import RedDot from '../../assets/red_dot.svg'
import YellowDot from '../../assets/yellow_dot.svg'
import { YEAR_BOUNDARY } from '../../constants/experimentYearBoundary'
import { PERMISSIONS } from '../../constants/permissions'
import { useFormValidation } from '../../hooks/useFormValidation'
import { hasPermission } from '../../utils/permissions'
import { CustomTooltip } from '../CustomTooltip'
import { CompletionLegacy } from '../formStepsLegacy/CompletionLegacy'
import { PlanningBasicsLegacy } from '../formStepsLegacy/PlanningBasicsLegacy/PlanningBasicsLegacy'
import { SetupDetailsLegacy } from '../formStepsLegacy/SetupDetailsLegacy'
import { ExperimentFormFooter } from './ExperimentFormFooter'
import { ExperimentFormToast, ToastMessage } from './ExperimentFormToast'

interface stepProps {
  id: 'planning_basics' | 'setup_details' | 'completion'
  name: 'Planning Basics' | 'Setup Details' | 'Analysis and Results'
}

const steps: stepProps[] = [
  {
    id: 'planning_basics',
    name: 'Planning Basics',
  },
  {
    id: 'setup_details',
    name: 'Setup Details',
  },
  {
    id: 'completion',
    name: 'Analysis and Results',
  },
]

export const ExperimentForm = ({ collapseForm }: { collapseForm: () => void }) => {
  const appStateContext = useContext(AppStateContext)
  const [convId, setConvId] = useState<string>('')
  const [activeStep, setActiveStep] = useState(0)
  const [isBackdropOpen, setIsBackdropOpen] = useState(false)
  const [isPhaseReminderModalOpen, setIsPhaseReminderModalOpen] = useState(false)
  const [isEnableEditingDialogOpen, setIsEnableEditingDialogOpen] = useState(false)
  const [messages, setMessages] = useState<Array<ToastMessage>>([])
  const canEditExperiment = hasPermission(PERMISSIONS.editExperiment)
  const isEditButtonDisabled = !appStateContext?.state.currentChat?.extended_data?.view_only || !canEditExperiment
  const year = appStateContext?.state.currentChat?.extended_data.experiment?.planning_basics.year.field_value || ''
  const isExperimentYearBeforeBoundary = !isEmpty(year) && parseInt(year, 10) <= YEAR_BOUNDARY

  const { setShouldScrollToError } = useFormValidation({
    appStateContext,
    steps,
    activeStep,
    setActiveStep,
  })

  const addMessage = useCallback((message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => {
    setMessages(prevMessages => [
      ...prevMessages,
      {
        id: uuidv4(),
        ...message,
      },
    ])
  }, [])

  const removeMessage = useCallback((id: string) => {
    setMessages(prevMessages => prevMessages.filter(message => message.id !== id))
  }, [])

  useEffect(() => {
    setConvId(appStateContext?.state.currentChat?.id || '')
  }, [appStateContext?.state.currentChat])

  useEffect(() => {
    if (appStateContext?.state.currentChat?.extended_data?.experiment_form_step) {
      setActiveStep(
        steps.findIndex(step => step.id === appStateContext?.state.currentChat?.extended_data?.experiment_form_step),
      )
    }
  }, [appStateContext?.state.currentChat?.extended_data?.experiment_form_step])

  const handleGoToPlanningBasics = async () => {
    const currentChat = appStateContext?.state.currentChat
    const field = currentChat?.extended_data?.experiment?.planning_basics?.experiment_phase_high_level

    if (field) {
      field.errors = ['Update recommended']
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: currentChat })
    }

    setActiveStep(0)
    setIsPhaseReminderModalOpen(false)
    setShouldScrollToError(true)
  }

  useEffect(() => {
    const current = appStateContext?.state.currentChat
    if (current?.extended_data?.user_confirmed_experiment_creation) {
      handleSubmitExperiment()
      current.extended_data.user_confirmed_experiment_creation = false
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: current })
    }
  }, [appStateContext?.state?.currentChat?.extended_data?.user_confirmed_experiment_creation])

  const updateActiveStep = async (newActiveStep: number) => {
    updateExperimentFormStep(steps[newActiveStep].id, convId)
      .then(res => {
        if (res?.hasFormErrors) {
          const messages = appStateContext?.state.currentChat?.messages || []
          res.messages = messages

          delete res.hasFormErrors
          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: res })
          appStateContext?.dispatch({ type: 'SET_SHOULD_VALIDATE_FORM', payload: true })

          setShouldScrollToError(true)
        } else if (res) {
          const messages = appStateContext?.state.currentChat?.messages || []
          res.messages = messages
          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: res })
          setActiveStep(newActiveStep)
        }
      })
      .catch(err => {
        addMessage({
          title: 'Error',
          description: err instanceof Error ? err.message : 'An error occurred, please try again.',
          type: 'error',
        })
      })
  }

  const handleSaveDraft = async () => {
    setIsBackdropOpen(true)
    await saveDraft(convId).then(res => {
      if (res?.hasFormErrors) {
        const messages = appStateContext?.state.currentChat?.messages || []
        res.messages = messages

        delete res.hasFormErrors
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: res })
      } else if (res) {
        const messages = appStateContext?.state.currentChat?.messages || []
        res.messages = messages

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: res })

        addMessage({
          title: 'Draft Saved',
          description: (
            <>
              Your experiment has been successfully stored in Experiment Tracker.{' '}
              <u>This is only a draft, not a submission</u>. Please submit at your earliest convenience.
              <br />
              <b>The following fields cannot be changed</b>: <i>Year, OU, Country, GLA Strategic Priority, E@S Experiment</i>{' '}
              and <i>Experiment Code</i>
            </>
          ),
          type: 'warning',
        })
      } else {
        addMessage({
          title: 'An error ocurred',
          description: 'Please try again. If the problem persists, please contact the site administrator.',
          type: 'error',
        })
      }
    })
    setIsBackdropOpen(false)
  }

  const shouldOpenPhaseReminderModal = () => {
    const experiment = appStateContext?.state.currentChat?.extended_data?.experiment
    if (!experiment) return false

    const hasErrors = Object.values(experiment).some(section =>
      Object.values(section || {}).some(
        field => field?.errors && field.errors.length > 0 && !field.errors.includes('Update recommended'),
      ),
    )

    return !hasErrors && experiment?.planning_basics?.experiment_phase_high_level?.field_value === 'Planned'
  }

  const handleSubmitExperiment = async (skipReminder = false) => {
    if (shouldOpenPhaseReminderModal() && !skipReminder) {
      setIsPhaseReminderModalOpen(true)
      return
    }

    setIsBackdropOpen(true)
    setIsPhaseReminderModalOpen(false)
    submitExperiment(convId)
      .then(res => {
        if (res?.hasFormErrors) {
          const messages = appStateContext?.state.currentChat?.messages || []
          res.messages = messages

          delete res.hasFormErrors
          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: res })
          appStateContext?.dispatch({ type: 'SET_SHOULD_VALIDATE_FORM', payload: true })

          setShouldScrollToError(true)
        } else if (res) {
          const messages = appStateContext?.state.currentChat?.messages || []
          res.messages = messages

          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: res })
          appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: res })

          addMessage({
            title: 'Experiment Submitted',
            description: (
              <>
                Your experiment was successfully submitted to Experiment Tracker. You can still edit it, but{' '}
                <b>the following fields cannot be changed</b>: <i>Year, OU, Country, GLA Strategic Priority, E@S Experiment</i>{' '}
                and <i>Experiment Code</i>
              </>
            ),
            type: 'success',
          })
        } else {
          addMessage({
            title: 'An error ocurred',
            description: 'Submission Failed! Please try again. If the error persists, contact support team.',
            type: 'error',
          })
        }
      })
      .finally(() => {
        setIsBackdropOpen(false)
      })
  }

  useEffect(() => {
    const planningBasics = appStateContext?.state.currentChat?.extended_data.experiment?.planning_basics
    const extraData = appStateContext?.state.currentChat?.extended_data.experiment?.extra_data

    if (
      !isEmpty(convId) &&
      !isEmpty(planningBasics?.ou.field_value) &&
      !isEmpty(planningBasics?.country.field_value) &&
      (!planningBasics?.is_e_at_s_experiment.field_value || !isEmpty(planningBasics?.mto_initiative?.field_value)) &&
      !isEmpty(planningBasics?.experiment_code.field_value) &&
      isEmpty(planningBasics?.experiment_code.errors) &&
      isNil(extraData?.id.field_value)
    ) {
      handleSaveDraft()
    }
  }, [
    convId,
    appStateContext?.state.currentChat?.extended_data.experiment?.planning_basics,
    appStateContext?.state.currentChat?.extended_data.experiment?.extra_data,
  ])

  const getExperimentStatusInfo = () => {
    const experimentData = appStateContext?.state.currentChat?.extended_data
    const status = experimentData?.experiment?.planning_basics.experiment_status.field_value

    if (isEmpty(status)) {
      return { dotSrc: GrayDot, text: 'Not Saved' }
    }

    const statusMap = {
      Draft: { dotSrc: YellowDot, text: 'Draft' },
      Submit: { dotSrc: BlueDot, text: 'Submitted' },
      'Pending Resubmission': { dotSrc: RedDot, text: 'Pending Resubmission' },
    }

    return get(statusMap, status as string) || { dotSrc: null, text: status }
  }

  const CustomStepIcon = (props: StepIconProps, title: number) => {
    const { active, completed } = props

    return (
      <div className={active || completed ? styles.stepIconCompleted : styles.stepIcon}>
        <Typography className={styles.stepIconText}>{title}</Typography>
      </div>
    )
  }

  const handleEditExperiment = () => {
    const chatId = appStateContext?.state.currentChat?.id
    if (!chatId) {
      console.error('Cannot edit experiment: chat ID is missing.')
      return
    }
    historyRead(chatId, undefined, false)
      .then(conversation => {
        if (!conversation) return

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
        appStateContext?.dispatch({ type: 'SET_SHOULD_VALIDATE_FORM', payload: false })
      })
      .catch(err => {
        console.error(`Error fetching chat history for experiment ${chatId}`, err)
      })
    setIsEnableEditingDialogOpen(false)
  }

  const closeEnableEditingDialog = () => setIsEnableEditingDialogOpen(false)

  const legacySteps = [<PlanningBasicsLegacy />, <SetupDetailsLegacy />, <CompletionLegacy />]
  const currentSteps = [
    <PlanningBasicsStep onError={addMessage} />,
    <SetupDetailsStep onError={addMessage} />,
    <AnalysisAndResultsStep onError={addMessage} />,
  ]
  const stepsToRender = isExperimentYearBeforeBoundary ? legacySteps : currentSteps

  return (
    <>
      <Box className={styles.container}>
        <CustomTooltip
          title="Collapse experiment form"
          placement="top">
          <IconButton
            className={styles.sideToggleButton}
            onClick={() => {
              collapseForm()
            }}
            aria-label="Toggle experiment form panel">
            <img src={CollapseButton} />
          </IconButton>
        </CustomTooltip>
        {/* Header */}
        <Box className={styles.stepperContainer}>
          <Stepper
            activeStep={activeStep}
            connector={
              <StepConnector
                classes={{
                  completed: styles.stepConnectorCompleted,
                  active: styles.stepConnectorCompleted,
                  line: styles.stepConnectorLine,
                }}
              />
            }>
            {steps.map((step, index) => {
              return (
                <Step
                  key={step.id}
                  classes={{
                    root: styles.stepRoot,
                  }}>
                  <StepLabel
                    classes={{
                      root: styles.stepLabelRoot,
                    }}
                    slots={{
                      stepIcon: (props: StepIconProps) => CustomStepIcon(props, index + 1),
                    }}
                  />
                </Step>
              )
            })}
          </Stepper>
        </Box>
        <div className={styles.experimentFormHeader}>
          <div className={styles.stepInfo}>
            <Typography className={styles.stepNumber}>{`Step ${activeStep + 1} of 3`}</Typography>
            <Typography className={styles.stepName}>
              {activeStep === 2 && isExperimentYearBeforeBoundary ? 'Completion' : steps[activeStep].name}
            </Typography>
          </div>
          <div className={styles.experimentInfo}>
            {!isExperimentYearBeforeBoundary && (
              <CustomTooltip
                title="Edit: Requires permission"
                placement="top"
                disableHoverListener={canEditExperiment}>
                <span>
                  <IconButton
                    className={styles.editExperimentButton}
                    disabled={isEditButtonDisabled}
                    onClick={() => setIsEnableEditingDialogOpen(true)}>
                    <img
                      src={EditSquareIcon}
                      className={isEditButtonDisabled ? styles.editExperimentButtonIconDisabled : ''}
                    />
                  </IconButton>
                </span>
              </CustomTooltip>
            )}

            <div className={styles.experimentStatusContainer}>
              <img src={getExperimentStatusInfo().dotSrc} />
              <Typography className={styles.experimentStatus}>{getExperimentStatusInfo().text}</Typography>
            </div>

            <Typography className={styles.experimentCode}>
              Code:{' '}
              {get(
                appStateContext?.state.currentChat?.extended_data.experiment?.planning_basics.experiment_code,
                'field_value',
                '-',
              )}
            </Typography>
          </div>
        </div>
        <ExperimentFormToast
          messages={messages}
          onMessageExpire={removeMessage}
        />
        <Fragment>
          <Container className={styles.formStepContainer}>{stepsToRender[activeStep]}</Container>
        </Fragment>
        <ExperimentFormFooter
          activeStep={activeStep}
          onChangeStep={updateActiveStep}
          onSubmitExperiment={() => handleSubmitExperiment(false)}
          isSubmitDisabled={Boolean(appStateContext?.state.currentChat?.extended_data?.view_only)}
          isSubmitHidden={isExperimentYearBeforeBoundary}
        />
        <CustomDialog
          title="Action Required"
          open={isPhaseReminderModalOpen}
          setClose={() => setIsPhaseReminderModalOpen(false)}
          primaryAction={handleGoToPlanningBasics}
          primaryActionButtonText={'Go to Planning Basics'}
          secondaryAction={() => handleSubmitExperiment(true)}
          secondaryActionButtonText={'No Updated Needed'}>
          <Typography className={styles.modalContentText}>
            You haven't updated the <i>Experiment Phase</i> status. Please navigate to <i>Planning Basics</i> to change
            the status from <b>Planned</b> to <b>Underway</b>.
          </Typography>
        </CustomDialog>
        <CustomDialog
          title="Enable Editing"
          open={isEnableEditingDialogOpen}
          setClose={closeEnableEditingDialog}
          primaryAction={handleEditExperiment}
          secondaryAction={closeEnableEditingDialog}
          primaryActionButtonText="Continue"
          secondaryActionButtonText="Cancel">
          <Typography className={styles.modalContentText}>
            Enabling editing allows direct changes to be made. Please note that all changes are{' '}
            <strong>auto-saved</strong>. To return to view-only mode, navigate back to the main list. Would you like to
            continue?
          </Typography>
        </CustomDialog>
      </Box>
      <Backdrop
        sx={theme => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={isBackdropOpen}>
        <div className={styles.backdropMessageContainer}>
          <CircularProgress color="inherit" />
          <p className={styles.backdropMessage}>Saving Experiment</p>
        </div>
      </Backdrop>
    </>
  )
}
